import * as React from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Third Party
import { User as Auth0User } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

// Internal
import { ReactionType, CommentType } from "../../../data/types";
import en from "../../../data/en.json";

interface FeedItemActionsProps {
  user?: Auth0User;
  reactions: ReactionType[];
  comments: CommentType[];
  onReact: () => void;
  onComment: (commentText: string) => void;
  // When omitted, the add-to-collection icon is not rendered (e.g. the
  // publication detail page, which has no collection menu).
  onAddToCollection?: (event: React.MouseEvent<HTMLElement>) => void;
  // When omitted, the flag icon and dialog are not rendered (e.g. publications).
  onFlag?: () => void;
}

// Shared action bar (react / comment / optional flag / add-to-collection) used by both
// community feed posts and publications. Click handlers stop propagation so the bar can
// be rendered alongside a navigating link without triggering navigation.
function FeedItemActions(props: FeedItemActionsProps) {
  const [flagDialogOpen, setFlagDialogOpen] = React.useState<boolean>(false);
  const [commentDialogOpen, setCommentDialogOpen] =
    React.useState<boolean>(false);
  const [commentText, setCommentText] = React.useState<string>("");

  const handleFlag = () => {
    props.onFlag?.();
    setFlagDialogOpen(false);
  };

  const handleComment = () => {
    props.onComment(commentText);
    setCommentDialogOpen(false);
    setCommentText("");
  };

  const stop = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderFavoriteIcon = () => {
    const reactedUserAuth0Ids: string[] = props.reactions.map(
      (reaction: ReactionType) => reaction.user.auth0Id
    );
    const reactIcon =
      props.user?.sub && reactedUserAuth0Ids.includes(props.user.sub) ? (
        <Badge badgeContent={props.reactions.length} color="primary">
          <FavoriteIcon />
        </Badge>
      ) : (
        <FavoriteBorderIcon />
      );

    return (
      <IconButton
        onClick={(e) => {
          stop(e);
          props.onReact();
        }}
      >
        {reactIcon}
      </IconButton>
    );
  };

  const renderCommentIcon = () => {
    const commentIcon =
      props.comments.length > 0 ? (
        <Badge badgeContent={props.comments.length} color="primary">
          <ChatBubbleIcon />
        </Badge>
      ) : (
        <ChatBubbleOutlineIcon />
      );

    return (
      <React.Fragment>
        <IconButton
          onClick={(e) => {
            stop(e);
            setCommentDialogOpen(true);
          }}
        >
          {commentIcon}
        </IconButton>

        <Dialog
          fullWidth
          open={commentDialogOpen}
          onClose={() => setCommentDialogOpen(false)}
        >
          <DialogTitle>{en.community.comments}</DialogTitle>

          <DialogContent>
            <TextField
              multiline
              fullWidth
              variant="standard"
              sx={{ maxHeight: 100, overflow: "hidden", overflowY: "scroll" }}
              label={en.community.whatIsOnYourMind}
              InputProps={{ disableUnderline: true }}
              defaultValue={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
              }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={() => handleComment()}>
                {en.community.comment}
              </Button>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              sx={{ maxHeight: 250, overflow: "hidden", overflowY: "scroll" }}
            >
              {props.comments.map((comment: CommentType, index: number) => (
                <Box key={index}>
                  <Box display="flex" flexDirection={"row"} sx={{ pt: 1 }}>
                    <Link
                      key={comment.user.handle}
                      to={`/users/${comment.user.handle}`}
                      target="_blank"
                      style={{ textDecoration: "none", color: "black" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", pr: 1 }}
                      >
                        {comment.user.profile?.nickname ||
                          en.community.unknownUsername}
                      </Typography>
                    </Link>
                    <Typography variant="body2">{comment.createdAt}</Typography>
                  </Box>
                  <Typography variant="body1">{comment.content}</Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={() => setCommentDialogOpen(false)}>
              {en.common.close}
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  };

  const renderFlagIcon = () => {
    if (!props.onFlag) return null;

    return (
      <React.Fragment>
        <IconButton
          onClick={(e) => {
            stop(e);
            setFlagDialogOpen(true);
          }}
        >
          <ErrorOutlineIcon />
        </IconButton>
        <Dialog open={flagDialogOpen} onClose={() => setFlagDialogOpen(false)}>
          <DialogTitle>{en.community.flagPostTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {en.community.flagPostDescription}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFlagDialogOpen(false)}>
              {en.common.cancel}
            </Button>
            <Button onClick={() => handleFlag()} autoFocus>
              {en.community.flag}
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection={"row"}
      justifyContent={"space-around"}
      sx={{ pt: 1 }}
    >
      {renderFavoriteIcon()}
      {renderCommentIcon()}
      {renderFlagIcon()}
      {props.onAddToCollection && (
        <IconButton
          onClick={(e) => {
            stop(e);
            props.onAddToCollection?.(e);
          }}
          title="Add to Collection"
        >
          <BookmarkAddIcon />
        </IconButton>
      )}
    </Box>
  );
}

export default FeedItemActions;
